const BACKEND_OFFLINE_MESSAGE =
  "Backend server not running. Check REACT_APP_API_BASE_URL and try again.";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function buildApiUrl(path) {
  if (!API_BASE_URL || /^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path}`;
}

function isBackendOfflineText(value) {
  return /proxy error|could not proxy request|econnrefused|econnreset|backend is unreachable/i.test(
    String(value || ""),
  );
}

import { flattenError } from "./utils";
import { logApiError } from "./discordLogger";

export async function fetchJson(url, options = {}) {
  const isFormData = options.body instanceof FormData;
  const baseHeaders = isFormData ? {} : { "Content-Type": "application/json" };
  baseHeaders["X-Requested-With"] = "XMLHttpRequest";

  let response;
  try {
    response = await fetch(buildApiUrl(url), {
      credentials: "include",
      ...options,
      headers: {
        ...baseHeaders,
        ...(options.headers || {}),
      },
    });
  } catch (networkError) {
    const wrapped = new Error(BACKEND_OFFLINE_MESSAGE);
    wrapped.cause = networkError;
    wrapped.isNetworkError = true;
    logApiError({ method: options.method || "GET", url, message: wrapped.message });
    throw wrapped;
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    let payload = null;
    const contentType = response.headers.get("content-type") || "";
    try {
      if (contentType.includes("application/json")) {
        payload = await response.json();
        message = flattenError(payload) || message;
      } else {
        const text = await response.text();
        if (text) {
          if (isBackendOfflineText(text)) {
            message = BACKEND_OFFLINE_MESSAGE;
          } else if (/<!doctype html|<html/i.test(text)) {
            message = `Unexpected HTML response (status ${response.status}).`;
          } else {
            message = text.slice(0, 500);
          }
        }
      }
    } catch (_parseError) {
      // fall through with default message
    }
    const errorObject = new Error(message);
    errorObject.payload = payload;
    errorObject.status = response.status;
    // Skip 401 — expected when session expires, handled by redirect to /login
    if (response.status !== 401) {
      logApiError({
        method: options.method || "GET",
        url,
        status: response.status,
        message,
        payload,
      });
    }
    throw errorObject;
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    if (/<!doctype html|<html/i.test(text)) {
      throw new Error("Server returned HTML instead of JSON.");
    }
    return text ? JSON.parse(text) : null;
  }

  return response.json();
}
